<template>
  <section class="result-hero" aria-labelledby="result-title">
    <p class="result-meta">
      <ph-icon name="link-simple" :size="14" />
      <a :href="uri" target="_blank" rel="noopener">{{ page }}</a>
      <template v-if="fieldCount"> · {{ fieldCount }} {{ fieldCount === 1 ? 'field' : 'fields' }}</template>
      <template v-if="scoredDate"> · scored {{ scoredDate }}</template>
    </p>
    <score-ring class="result-ring" :score="score" :grade="grade.label" />
    <span class="result-grade" :class="`result-grade--${grade.chip}`">
      <span class="result-grade-dot" :style="{ background: `var(--ex-score-${level})` }" aria-hidden="true" />
      {{ grade.label }}
    </span>
    <h1 id="result-title" class="result-headline">{{ grade.headline }}</h1>
    <p class="result-line">
      {{ grade.line.before }}<strong v-if="grade.line.weak">{{ grade.line.weak }}</strong>{{ grade.line.after }}
    </p>
    <score-scale class="result-scale" :score="score" :grade="grade.key" />
    <ul class="result-chips" aria-label="Score per category">
      <li v-for="category in scoredCategories" :key="category.name" class="result-chip">
        <span class="result-chip-dot" :style="{ background: `var(--ex-score-${category.level})` }" aria-hidden="true" />
        {{ category.label }}
        <span class="result-chip-score">{{ category.score }}</span>
      </li>
    </ul>
    <div class="result-actions no-print">
      <a class="result-share" :href="linkedInHref" target="_blank" rel="noopener" aria-label="Share on LinkedIn"><ph-icon name="linkedin-logo" :size="16" /></a>
      <a class="result-share" :href="xHref" target="_blank" rel="noopener" aria-label="Share on X"><ph-icon name="x-logo" :size="16" /></a>
      <a class="result-share" :href="mailHref" aria-label="Share by email"><ph-icon name="envelope-simple" :size="16" /></a>
      <button type="button" class="result-share" :aria-label="linkCopied ? 'Link copied' : 'Copy link'" @click="copyLink">
        <ph-icon :name="linkCopied ? 'check' : 'copy'" :size="16" />
      </button>
      <button type="button" class="ex-btn ex-btn--secondary ex-btn--small result-restart" @click="$emit('restart')">
        <span class="ex-btn__label" data-label="Score another form">Score another form</span>
      </button>
    </div>
    <div class="print-only result-print">
      <qrcode-vue :value="shareUri" size="120" level="H" />
      <p>Find out how your forms score at <strong>score.exatom.io</strong></p>
    </div>
  </section>
</template>

<script>
import QrcodeVue from 'qrcode.vue'
import PhIcon from './PhIcon.vue'
import ScoreRing from './ScoreRing.vue'
import ScoreScale from './ScoreScale.vue'
import { formatScore, getCategoryLabel, getScoreLevel, isScoredCategory } from '../score/report.mjs'

const SHARE_TEXT = 'I just scored my form with Exatom'

export default {
  name: 'ResultHero',
  components: { PhIcon, QrcodeVue, ScoreRing, ScoreScale },
  props: {
    score: { type: [Number, String], required: true },
    details: { type: Object, required: true },
    grade: { type: Object, required: true },
    uri: { type: String, required: true },
    shareUri: { type: String, required: true },
    fieldCount: { type: Number, default: 0 },
    scoredDate: { type: String, default: '' }
  },
  data () {
    return { linkCopied: false }
  },
  computed: {
    page () {
      return this.uri.replace(/^https?:\/\/(www\.)?/i, '')
    },
    level () {
      return getScoreLevel(this.score)
    },
    scoredCategories () {
      return Object.entries(this.details)
        .filter(([, category]) => isScoredCategory(category))
        .map(([name, category]) => ({
          name,
          label: getCategoryLabel(name),
          score: formatScore(category.score),
          level: getScoreLevel(category.score)
        }))
    },
    linkedInHref () {
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(this.shareUri)}`
    },
    xHref () {
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TEXT)}&url=${encodeURIComponent(this.shareUri)}`
    },
    mailHref () {
      return `mailto:?subject=${encodeURIComponent('Our form score by Exatom')}&body=${encodeURIComponent(this.shareUri)}`
    }
  },
  methods: {
    async copyLink () {
      await navigator.clipboard.writeText(this.shareUri)
      this.linkCopied = true
      setTimeout(() => {
        this.linkCopied = false
      }, 1500)
    }
  }
}
</script>

<style scoped>
.result-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 64px 64px;
  background: var(--ex-color-surface-blue);
  text-align: center;
}

.result-meta {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0 6px;
  font-size: 14px;
  color: var(--ex-color-neutral-500);
}

.result-meta a {
  color: inherit;
  text-decoration: none;
  overflow-wrap: anywhere;
}

.result-meta a:hover {
  text-decoration: underline;
}

.result-ring {
  margin-top: 24px;
}

.result-grade {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  margin-top: 20px;
  padding: 0 12px;
  border-radius: var(--ex-radius-pill);
  font-size: 14px;
  font-weight: 600;
}

.result-grade--excellent {
  background: var(--ex-grade-excellent-bg);
  color: var(--ex-grade-excellent-ink);
}

.result-grade--good {
  background: var(--ex-grade-good-bg);
  color: var(--ex-grade-good-ink);
}

.result-grade--fair {
  background: var(--ex-grade-fair-bg);
  color: var(--ex-grade-fair-ink);
}

.result-grade--poor {
  background: var(--ex-grade-poor-bg);
  color: var(--ex-grade-poor-ink);
}

.result-grade-dot,
.result-chip-dot {
  width: 8px;
  height: 8px;
  border-radius: var(--ex-radius-pill);
}

.result-headline {
  margin-top: 14px;
  font-size: 52px;
  letter-spacing: -0.04em;
  line-height: 1.05;
}

.result-headline:focus {
  outline: none;
}

.result-line {
  max-width: 620px;
  margin-top: 12px;
  font-size: 18px;
  line-height: 1.55;
  color: var(--ex-color-neutral-700);
}

.result-line strong {
  font-weight: 600;
}

.result-scale {
  margin-top: 28px;
}

.result-chips {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin: 32px 0 0;
  padding: 0;
  list-style: none;
}

.result-chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 16px;
  border: 1px solid var(--ex-color-hairline);
  border-radius: var(--ex-radius-pill);
  background: var(--ex-color-white);
  font-size: 14px;
  font-weight: 600;
}

.result-chip-dot {
  width: 10px;
  height: 10px;
}

.result-chip-score {
  font-family: var(--ex-font-heading);
  color: var(--ex-color-neutral-700);
}

.result-actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
}

.result-share {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 1px solid var(--ex-color-neutral-100);
  border-radius: var(--ex-radius-pill);
  background: transparent;
  color: var(--ex-color-dark);
  cursor: pointer;
}

.result-share:hover {
  border-color: var(--ex-color-dark);
  color: var(--ex-color-dark);
}

.result-restart {
  margin-left: 4px;
}

.result-print {
  margin-top: 24px;
}

@media (max-width: 960px) {
  .result-hero {
    padding: 40px 16px 48px;
  }

  .result-headline {
    font-size: 36px;
  }

  .result-line {
    font-size: 16px;
  }
}
</style>
